import { useMutation, useQueryClient } from '@tanstack/react-query';

import useAPI from '@/hooks/useAPI';
import CacheConsoleRepository from '@/repositories/CacheConsoleRepository';

// SCAN is engineer-driven (typed pattern, clicked Next) — never poll it.
// The cursor is opaque to the UI: pass back what /scan returned to continue.
export const useCacheScan = (pattern: string, cursor: number, count?: number) => {
  const repo = CacheConsoleRepository();
  return useAPI({
    queryKey: ['cacheScan', pattern, cursor, count ?? null],
    queryFn: () => repo.scan(pattern, cursor, count)
  });
};

export const useKnownCacheKeys = () => {
  const repo = CacheConsoleRepository();
  return useAPI({
    queryKey: ['cacheKnownKeys'],
    queryFn: () => repo.knownKeys()
  });
};

// One-shot fetch: pulls summary + preview in one round trip. Only fires when
// a key is selected; cache is keyed so re-selecting the same key is instant.
export const useCacheInspect = (key: string | null) => {
  const repo = CacheConsoleRepository();
  return useAPI({
    queryKey: ['cacheInspect', key],
    queryFn: () => repo.inspect(key ?? ''),
    enabled: Boolean(key)
  });
};

export const useDeleteCacheKey = () => {
  const repo = CacheConsoleRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => repo.deleteKey(key),
    onSuccess: () => {
      // The deleted key drops out of any open scan page and the inspect cache
      // becomes stale — invalidate both so the UI catches up.
      queryClient.invalidateQueries({ queryKey: ['cacheScan'] });
      queryClient.invalidateQueries({ queryKey: ['cacheInspect'] });
    }
  });
};
